import type { ReactNode } from "react";
import { TRUST_BOUNDARY } from "../domain/constants";
import { VetoLogo } from "./VetoLogo";

type Props = {
  eyebrow: string;
  title: string;
  subtitle?: string;
  fileMeta?: string;
  children: ReactNode;
  darkHeader?: boolean;
  actions?: ReactNode;
  compact?: boolean;
};

/** Document-first layout: black band header, warm paper body, one highlight accent. */
export function VetoPageShell({
  eyebrow,
  title,
  subtitle,
  fileMeta,
  children,
  darkHeader,
  actions,
  compact = false
}: Props) {
  const widthClass = compact ? "max-w-xl" : "max-w-6xl";

  return (
    <div className="min-h-[100dvh] bg-[#f2f1ec] text-[#17201b]">
      <header
        className={
          darkHeader
            ? "border-b border-[#17201b] bg-[#17201b] px-4 py-5 text-white sm:px-5"
            : "border-b border-[#d9d8d0] bg-[#fffefa] px-4 py-4 sm:px-5 sm:py-5"
        }
      >
        <div className={`mx-auto flex ${widthClass} flex-col gap-4 lg:flex-row lg:items-end lg:justify-between`}>
          <div className="min-w-0">
            <div className="flex items-center justify-between gap-4">
              <VetoLogo height={compact ? 24 : 30} onDark={darkHeader} />
              {compact ? (
                <p
                  className={`truncate text-[11px] font-bold uppercase tracking-[0.1em] ${
                    darkHeader ? "text-[#b8c4bc]" : "text-[#5c665f]"
                  }`}
                >
                  {eyebrow}
                </p>
              ) : null}
            </div>
            {!compact ? (
            <p
              className={`mt-3 text-xs font-bold uppercase tracking-[0.12em] ${
                darkHeader ? "text-[#b8c4bc]" : "text-[#5c665f]"
              }`}
            >
              {eyebrow}
            </p>
            ) : null}
            <h1 className={`${compact ? "mt-3 text-[1.65rem]" : "mt-1 text-2xl lg:text-3xl"} font-bold leading-tight tracking-[-0.025em]`}>
              {title}
            </h1>
            {subtitle ? (
              <p className={`mt-2 max-w-3xl text-sm leading-relaxed ${darkHeader ? "text-[#d8e0dc]" : "text-[#5c665f]"}`}>
                {subtitle}
              </p>
            ) : null}
            {fileMeta ? (
              <p
                className={`mt-3 border-t pt-3 text-[13px] font-semibold leading-snug ${
                  darkHeader ? "border-white/15 text-white" : "border-[#e3e1da] text-[#27332c]"
                }`}
              >
                {fileMeta}
              </p>
            ) : null}
          </div>
          {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
        </div>
      </header>
      <main className={`mx-auto grid ${widthClass} gap-3 px-4 py-4 sm:gap-4 sm:px-5 sm:py-5`}>{children}</main>
      {!compact ? (
        <footer className={`mx-auto ${widthClass} px-4 pb-7 pt-1 sm:px-5`}>
          <p className="text-[11px] leading-relaxed text-[#69716c]">{TRUST_BOUNDARY}</p>
        </footer>
      ) : null}
    </div>
  );
}
